import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { clientDatabasesAPI } from '@hpanel/web-common/api/client'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'
import type { ClientDatabase, Domain } from '@hpanel/web-common/api/types'
import { ConnectionDetailsModal } from './ConnectionDetailsModal'

/**
 * The /client/databases/new form; drives `POST /api/client/databases`.
 * A routed page, not a modal (CLAUDE.md §15) — it has five inputs.
 *
 * UX intent (per the W2-D plan):
 *   - The user types a *suffix*, not a full DB name. The full name is
 *     `<tenant_prefix>_<suffix>` and the prefix comes from the
 *     authenticated user's slug. We preview the resulting name in real
 *     time so the user knows exactly what'll be created — eliminates
 *     "wait, why did it call my DB tenant_blog instead of blog?".
 *   - Same shape for the primary user suffix.
 *   - Password + confirm; min 12 enforced server-side and pre-validated
 *     here so users see the constraint without a round-trip.
 *
 * §9 contract: errors come from fetchAPI which preserves the server's
 * `detail` (e.g. "Suffix must match [a-z0-9_]+"); we render that text
 * verbatim. A 503 stays inline too, with what the user typed intact: it
 * used to swap the whole page for a "starting up, try again" banner, which
 * lost the form and sent customers into a retry loop for what is usually
 * a configuration problem only their provider can fix.
 */

export interface CreateDatabaseFormProps {
  /** null while loading — the no-domains hint must not flash. */
  domains: Domain[] | null
  onClose: () => void
}

/**
 * Mirror of the backend `derive_tenant_prefix(user)` in
 * `packages/core/hpanel_core/core/databases/naming.py`. We replicate
 * here for the preview only — the server is the single source of
 * truth, but the user expects to see what'll happen before clicking
 * Create.
 */
function deriveTenantPrefix(username: string | null | undefined): string {
  if (!username) return ''
  const lower = username.toLowerCase()
  // Non-alnum → '_'.
  const safe = lower.replace(/[^a-z0-9]/g, '_')
  return safe.slice(0, 16)
}

export const CreateDatabaseForm: React.FC<CreateDatabaseFormProps> = ({
  domains,
  onClose,
}) => {
  const { user } = useAuth()
  const tenantPrefix = useMemo(
    () => deriveTenantPrefix(user?.username),
    [user?.username],
  )

  const [suffix, setSuffix] = useState('')
  const [primaryUserSuffix, setPrimaryUserSuffix] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [domainId, setDomainId] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Plan 18 §C: after a successful create, swap the form for a
  // connection-details panel showing the user WHERE to point their
  // app's DB_HOST/DB_PORT. We hold the created DB locally rather than
  // re-fetching — the create response already carries connection_info.
  const [createdDb, setCreatedDb] = useState<ClientDatabase | null>(null)

  const fullDbName = suffix ? `${tenantPrefix}_${suffix}` : ''
  const fullUserName = primaryUserSuffix
    ? `${tenantPrefix}_${primaryUserSuffix}`
    : ''

  // Pre-validation that mirrors the schema — user gets instant feedback
  // rather than a 422 round-trip.
  const suffixValid = /^[a-z0-9_]{1,47}$/.test(suffix)
  const userSuffixValid = /^[a-z0-9_]{1,47}$/.test(primaryUserSuffix)
  const passwordTooShort = password.length > 0 && password.length < 12
  const passwordMismatch = confirm.length > 0 && password !== confirm
  const canSubmit =
    !busy &&
    suffixValid &&
    userSuffixValid &&
    domainId !== '' &&
    password.length >= 12 &&
    password === confirm

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const created = await clientDatabasesAPI.create({
        domain_id: Number(domainId),
        suffix,
        db_type: 'mysql',
        primary_user_suffix: primaryUserSuffix,
        primary_password: password,
      })
      // Clear sensitive state. We do NOT keep the password around to
      // render in the success view — the user just typed it; showing it
      // again only adds screen-capture / shoulder-surf risk for no
      // win. The success view shows host/port/name/user only.
      setPassword('')
      setConfirm('')
      // `onClose` waits until the user clicks Done on the success view.
      setCreatedDb(created)
    } catch (e) {
      const apiErr = e as { status?: number; message?: string }
      // eslint-disable-next-line no-console
      console.error('[CreateDatabaseForm] create failed', e)
      const message = e instanceof Error ? e.message : 'Failed to create database'
      setError(
        apiErr.status === 503
          ? `${message.replace(/\.$/, '')}. This is a problem on the server, not with your input — contact your hosting provider if it persists.`
          : message,
      )
    } finally {
      setBusy(false)
    }
  }

  // Success view: same wrapper, ConnectionDetailsModal is reused so the
  // "where do I point my app at" affordance has one canonical visual.
  if (createdDb) {
    return (
      <div data-testid="create-database-modal" data-state="success">
        <ConnectionDetailsModal db={createdDb} onClose={onClose} />
      </div>
    )
  }

  return (
    <div data-testid="create-database-modal" className="max-w-2xl">
      <div className="card p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
          Create Database
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          A primary user is provisioned with the database. Add more
          users with custom permissions afterwards.
        </p>

        {domains !== null && domains.length === 0 && (
          <div
            data-testid="create-no-domains"
            className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 text-sm text-amber-800 dark:text-amber-200"
          >
            A database belongs to a domain, and you have none yet.{' '}
            <Link to="/client/sites/request" className="font-medium underline">
              Request a site first
            </Link>
            .
          </div>
        )}

        {error && (
          <div
            data-testid="create-error"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Database suffix → preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Database Suffix
            </label>
            <input
              type="text"
              required
              autoFocus
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              pattern="[a-z0-9_]+"
              maxLength={47}
              data-testid="create-suffix-input"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="blog"
            />
            <p
              data-testid="create-db-name-preview"
              className="text-xs text-gray-500 dark:text-gray-400 mt-1"
            >
              {fullDbName ? (
                <>
                  Will be created as:{' '}
                  <span className="font-mono text-gray-700 dark:text-gray-200">
                    {fullDbName}
                  </span>
                </>
              ) : (
                <>
                  Lowercase letters, digits, and underscores only.
                  Server prefixes with{' '}
                  <span className="font-mono">
                    {tenantPrefix || '<your_tenant>'}
                  </span>
                  _.
                </>
              )}
            </p>
          </div>

          {/* Domain select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Domain
            </label>
            <select
              required
              value={domainId}
              onChange={(e) => setDomainId(e.target.value)}
              data-testid="create-domain-select"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a domain</option>
              {(domains ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Primary user suffix → preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Primary User Suffix
            </label>
            <input
              type="text"
              required
              value={primaryUserSuffix}
              onChange={(e) => setPrimaryUserSuffix(e.target.value)}
              pattern="[a-z0-9_]+"
              maxLength={47}
              data-testid="create-user-suffix-input"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="owner"
            />
            <p
              data-testid="create-user-name-preview"
              className="text-xs text-gray-500 dark:text-gray-400 mt-1"
            >
              {fullUserName ? (
                <>
                  Username:{' '}
                  <span className="font-mono text-gray-700 dark:text-gray-200">
                    {fullUserName}
                  </span>
                </>
              ) : (
                'The primary user has full access to this database.'
              )}
            </p>
          </div>

          {/* Password + confirm */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Primary Password
            </label>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="create-password-input"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="At least 12 characters"
            />
            {passwordTooShort && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                Minimum 12 characters.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="create-password-confirm"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Repeat the password"
            />
            {passwordMismatch && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                Passwords do not match.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="create-cancel"
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="create-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Creating…' : 'Create Database'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
